import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getEnvelopeDetails, revisitEnvelope } from '../../api/settings';
import dayjs from 'dayjs';
import { Link } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

export default function TermsAndConditions({ companyId }) {
  const [signedDate, setSignedDate] = useState(null);
  const [envelopeId, setEnvelopeId] = useState(null);
  const [docusignLoading, setDocusignLoading] = useState(false);

  const openEnvelope = async () => {
    setDocusignLoading(true);
    const details = await revisitEnvelope(envelopeId, companyId);
    setDocusignLoading(false);
    window.open(details.redirect_uri, '_blank');
  };

  useEffect(() => {
    if (!companyId) {
      setSignedDate('Not available');
      setEnvelopeId('Not available');
      return;
    }
    getEnvelopeDetails(companyId)
      .then((response) => {
        if (response?.[0]?.docusignSignedDate) {
          setSignedDate(dayjs(response[0].docusignSignedDate).format('DD/MM/YYYY HH:mm'));
          setEnvelopeId(response[0].docusignEnvelopeId);
        } else {
          setSignedDate('Not available');
          setEnvelopeId('Not available');
        }
      })
      .catch(() => {
        setSignedDate('Not available');
        setEnvelopeId('Not available');
      });
  }, [companyId]);

  return (
    <>
      {docusignLoading && (
        <Backdrop sx={() => ({ color: '#fff', zIndex: 999999999 })} open={docusignLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <div className="termsAndCondition">
        <h1>Terms and Conditions</h1>
        <div className="rowterms">
          <div className="columnterms">
            <div className="blue-column">Envelope ID:</div>
          </div>
          <div className="columnterms">
            <div className="green-column">
              {sessionStorage.getItem('companyid') === '1' && envelopeId && envelopeId !== 'Not available' ? (
                <Link sx={{ cursor: 'pointer' }} onClick={openEnvelope}>
                  {envelopeId}
                </Link>
              ) : (
                envelopeId
              )}
            </div>
          </div>
        </div>

        <div className="rowterms">
          <div className="columnterms">
            <div className="blue-column">Accepted Date and Time</div>
          </div>
          <div className="columnterms">
            <div className="green-column">{signedDate}</div>
          </div>
        </div>
        <p>Version: 1.01 01-12-2024 EORI.UK</p>
        <section>
          <h2>1. These terms</h2>
          <h3>1.1 What these terms cover</h3>
          <p>
            These are the terms on which you may use our website and receive any services that are referred to in Clause
            5.1 below that we perform for you or for any company or organisation on whose behalf you act. Those services
            are referred to in these terms as "our services”.
          </p>

          <h3>1.2 Your authority to act on behalf of an organisation</h3>
          <p>
            To the extent that you purport to act on behalf of a company or organisation, by agreeing to these terms and
            conditions you confirm to us that you are authorised by that company or organisation to act on their behalf
            and that these terms and conditions bind that company or organisation too.
          </p>

          <h3>1.3 Why you should read these terms</h3>
          <p>
            Please read these terms carefully before you seek to utilise our services. These terms tell you who we are,
            how we will provide our services, how you and we may change or end the contract, what to do if there is a
            problem, and other important information. If you think that there is a mistake in these terms, please
            contact us to discuss.
          </p>

          <h3>1.4 Changing the terms</h3>
          <p>
            We may amend these terms from time to time, including adding new rules or terms governing use of or access
            to certain parts of our website and/or the provision of our services. Every time you wish to use our website
            or access our services, please check these terms to ensure you are familiar with and understand the terms
            that apply at that time. Your continued use of our website will constitute your acceptance of any amended
            terms. Where we consider the changes we are making to these terms are significant, we will notify you of the
            changes.
          </p>
        </section>

        <section>
          <h2>2. Information about us and how to contact us</h2>
          <h3>2.1 Who we are</h3>
          <p>
            We are EORI (UK) Limited, a company registered in England and Wales. Our company registration number is
            10666593, and our registered office is at Seafield Lane, Beoley, Redditch, Worcs, B98 9DB.
          </p>

          <h3>2.2 How to contact us</h3>
          <p>You can contact us by telephoning our customer service team using the details on the website.</p>

          <h3>2.3 How we may contact you</h3>
          <p>
            If we have to contact you, we will do so by telephone or by writing to you at the email address or postal
            address you provided to us in your registration.
          </p>

          <h3>2.4 "Writing" includes emails</h3>
          <p>
            When we use the words "writing" or "written" in these terms, this includes emails or other electronic
            text-based communication.
          </p>
        </section>

        <section>
          <h2>3. Registration</h2>
          <h3>3.1 Becoming a Registered User</h3>
          <p>
            In order to access certain areas of our website, you will need to become a Registered User. For the purposes
            of these terms, a "Registered User" is a user who has registered an account on the website ("Account"). You
            can only register for an Account if you are aged eighteen (18) years or older and intend to access the
            services provided through this website for commercial or business purposes.
          </p>

          <h3>3.2 Acceptance of terms</h3>
          <p>
            By registering for an Account, you are agreeing that you have read and accepted these terms and our privacy
            policy which can be found at www.eori.uk and are willing for us to deal with your data as set out in that
            policy and be bound by these terms.
          </p>

          <h3>3.3 Responsibilities for Account Information</h3>
          <p>
            You agree not to create an Account using a false identity or information. You further agree that you will
            not have more than one Account at any one time.
          </p>

          <h3>3.4 Suspension of access</h3>
          <p>
            If we believe the information you provide is not correct, current, or complete, or is an impersonation of
            someone else, we have the right to refuse, terminate, or suspend your access to sections of our website or
            any of its resources all without prior notice, although where we consider it appropriate, we may first seek
            further detail and information from you.
          </p>
        </section>

        <section>
          <h2>4. Our contract with you</h2>
          <h3>4.1 Acceptance of services</h3>
          <p>We will provide you with access to our website once you are a Registered User...</p>

          <h3>4.2 If we cannot accept your request</h3>
          <p>If we are unable to accept your request for us to provide services...</p>

          <h3>4.3 Your reference number</h3>
          <p>We will assign a reference number to you and/or to the services...</p>
        </section>

        <section>
          <h2>5. Our services</h2>
          <h3>5.1 Description of services</h3>
          <p>
            We may provide services to you in connection with the transport or transit of goods arriving or departing
            from Great Britain, Northern Ireland, the Republic of Ireland, or any other EU Member States, as agreed in
            advance. The services are as described on our website from time to time and may include the provision of
            training to aid your understanding of your obligations regarding transport or transit of goods and
            associated customs obligations.
          </p>

          <h3>5.2 Acting as a representative</h3>
          <p>
            Where agreed between us, we may act for you in the capacity of a "Direct Representative" or an "Indirect
            Representative" in accordance with Articles 18 and 19 of Regulation (EU) No. 952/2013 (as amended,
            superseded, or replaced from time to time) in relation to certain consignments of goods arriving or
            departing from Great Britain and/or Northern Ireland and/or the Republic of Ireland.
          </p>

          <h3>5.3 Termination of services</h3>
          <p>
            Where we have reasonable grounds to suspect that a consignment of goods is "Unacceptable," we may terminate
            the provision of services to you. A consignment of goods shall be deemed "Unacceptable" for the purposes of
            this provision under certain conditions described in Clause 5.3.
          </p>

          <h3>5.4 Confidentiality</h3>
          <p>
            We will treat the information you provide to us as confidential information and will only use or share it as
            required to provide the services.
          </p>
        </section>

        <section>
          <h2>6. For GB Entry Summary Declarations ("ENS") movements using sea, rail, and maritime</h2>
          <h3>6.1 ENS submissions</h3>
          <p>
            You may wish to use us to make ENS submissions on your behalf for goods being moved using sea, rail, or
            maritime. Where you choose to do so and where we agree to provide this service to you, we agree to lodge the
            relevant ENS declarations on your behalf using the information that you provide to us for this purpose.
          </p>
        </section>

        <section>
          <h2>7. Ending the contract</h2>
          <h3>7.1 Termination by us</h3>
          <p>
            This contract will commence as described in clause 4.1 above and will continue to cover all services we
            provide to you under it from time to time. We may choose to end this contract at any time and for any
            reason.
          </p>

          <h3>7.2 Termination by you</h3>
          <p>
            You may exercise your right to end this contract by discontinuing use of our services and deactivating your
            Account.
          </p>
        </section>

        <section>
          <h2>8. If there is a problem with the services</h2>
          <h3>8.1 Contact us</h3>
          <p>
            If you have any questions or complaints about the services, please contact us using the contact information
            on our website.
          </p>
        </section>

        <section>
          <h2>9. Payment</h2>
          <h3>9.1 Disbursements</h3>
          <p>
            In order to provide the services, we may be required to make certain payments on your behalf to HM Revenue &
            Customs or other competent authorities. You shall promptly pay to us any Disbursements we incur in
            connection with the services we provide to you.
          </p>
        </section>

        <section>
          <h2>10. Our responsibility for loss or damage suffered by you</h2>
          <h3>10.1 Limitations of liability</h3>
          <p>
            We shall only be liable for losses or damage suffered by you where such loss or damage arises from our
            negligence or Wilful Misconduct.
          </p>

          <h3>10.2 No exclusion of liability</h3>
          <p>
            We do not exclude or limit in any way our liability to you where it would be unlawful to do so. This
            includes liability for death or personal injury caused by our negligence or the negligence of our employees,
            agents, or subcontractors.
          </p>
        </section>

        <section>
          <h2>11. How we may use your personal information</h2>
          <h3>11.1 Use of personal information</h3>
          <p>
            We will only use and disclose your personal information in order to provide the services to you and as
            required in order to provide information to HM Revenue & Customs and other competent authorities as set out
            in our privacy policy.
          </p>
        </section>

        <section>
          <h2>12. You must keep your account details safe</h2>
          <h3>12.1 Confidentiality of login details</h3>
          <p>
            If you choose, or you are provided with, a user identification code, password, or any other piece of
            information as part of our security procedures, you must treat such information as confidential.
          </p>
        </section>

        <section>
          <h2>13. How you may use material on our site</h2>
          <h3>13.1 Intellectual property rights</h3>
          <p>
            We are the owner or the licensee of all intellectual property rights in our website, and in the material
            published on it. Those works are protected by database and/or copyright laws and treaties around the world.
            All such rights are reserved.
          </p>
        </section>

        <section>
          <h2>14. Other important terms</h2>
          <h3>14.1 We may transfer this agreement to someone else</h3>
          <p>
            We may transfer our rights and obligations under these terms to another organisation. We will always tell
            you in writing if this happens and we will ensure that the transfer will not affect your rights under the
            contract.
          </p>

          <h3>14.2 You need our consent to transfer your rights to someone else</h3>
          <p>
            You may only transfer your rights or your obligations under these terms to another person if we agree to
            this in writing (consent not to be unreasonably withheld or delayed).
          </p>
        </section>
      </div>
    </>
  );
}

TermsAndConditions.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
